import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Receipt, Smartphone, Loader2, X, Download, Search, User, Send, AlertTriangle, FileText } from 'lucide-react';
import { formatMoeda } from '@fiodecobre/shared';
import { api, getToken } from '../../lib/api';
import { useDialog } from '../DialogProvider';

// Comprovante de Venda (SEM VALOR FISCAL) — DA-290. Documento nao fiscal pra dar ao
// cliente prova do pagamento quando a NF nao saiu (SEFAZ instavel). Chaveia por
// orcamento_id (Caminho B). Imprimir (cartao default + link tradicional), Baixar PDF
// e Enviar WhatsApp. NAO fecha por clique no overlay (so X / Fechar / Esc).
//
// O botao de WhatsApp SEMPRE abre o seletor de destinatario (DA-347): este modal nasceu
// duas semanas ANTES daquela decisao e ficou de fora da varredura, seguindo com o disparo
// direto pro numero do cadastro (toast cancelavel de 3s). E o mesmo documento indo pro
// mesmo cliente que a nota — entregar documento e irreversivel e quem finaliza esta com o
// cliente na frente. Nao reintroduzir o disparo direto por conveniencia.
//
// Desenhado num PORTAL pro <body> (DA-458): overlay `fixed` so se apoia na TELA enquanto
// nenhum ancestral tiver transform/filter/contain — no <body> ele nao tem ancestral.

export interface ComprovanteVendaInfo {
  orcamentoId: number;
  clienteNome?: string | null;
  clienteWhatsapp?: string | null;
  valorTotal?: string | number | null;
  vendaNumero?: string | null;
  orcamentoNumero?: string | null;
  // A venda ja tem NF autorizada? So muda o aviso desta tela — o do PAPEL o backend deriva
  // do banco sozinho (a frase "a NF sera emitida quando a SEFAZ normalizar" e falsa quando
  // a nota ja saiu, e o papel vai pro cliente).
  notaAutorizada?: boolean;
}

interface Props {
  info: ComprovanteVendaInfo;
  onFechar: () => void;
}

interface ContatoSugestao {
  id: number;
  nome: string;
  push_name: string | null;
  telefone: string | null;
  jid: string;
  foto_url: string | null;
}

function formatPhone(digits: string): string {
  const local = digits.startsWith('55') ? digits.slice(2) : digits;
  if (local.length === 11) return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  if (local.length === 10) return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  return digits;
}

export function ModalComprovanteVenda({ info, onFechar }: Props) {
  const { orcamentoId } = info;
  const dialog = useDialog();
  const [imprimindo, setImprimindo] = useState(false);
  const [baixando, setBaixando] = useState(false);

  const [enviadoPara, setEnviadoPara] = useState<string | null>(null);
  const [modalBuscaAberto, setModalBuscaAberto] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onFechar(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  async function imprimir(formato: 'cartao' | 'tradicional' = 'cartao') {
    // Abre janela vazia ANTES do await (senao o browser bloqueia o popup pos-promise).
    const win = window.open('', '_blank');
    setImprimindo(true);
    try {
      const token = getToken();
      const r = await fetch(`/api/comprovante-venda/${orcamentoId}?formato=${formato}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(String(r.status));
      const blob = await r.blob();
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      if (win) win.location.href = url;
      else window.open(url, '_blank');
    } catch (e) {
      if (win) win.close();
      void dialog.alerta('Erro ao gerar comprovante: ' + (e as Error).message);
    } finally {
      setImprimindo(false);
    }
  }

  async function baixarPdf() {
    setBaixando(true);
    try {
      const token = getToken();
      const r = await fetch(`/api/comprovante-venda/${orcamentoId}?formato=cartao&download=1`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(String(r.status));
      const blob = await r.blob();
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `comprovante-${info.vendaNumero ?? info.orcamentoNumero ?? orcamentoId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      void dialog.alerta('Erro ao baixar comprovante: ' + (e as Error).message);
    } finally {
      setBaixando(false);
    }
  }

  const refVenda = info.vendaNumero
    ? `${info.vendaNumero}${info.orcamentoNumero ? ` (${info.orcamentoNumero})` : ''}`
    : (info.orcamentoNumero ?? null);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
          {/* Header */}
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
              <FileText size={22} className="text-gray-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-gray-800 text-lg">Comprovante não fiscal</div>
              <div className="text-xs font-semibold text-gray-500">Documento sem valor fiscal — não é Nota Fiscal</div>
              {info.clienteNome && (
                <div className="text-sm font-medium text-gray-700 truncate">{info.clienteNome}</div>
              )}
            </div>
          </div>

          <div className="text-sm text-gray-600 space-y-1 bg-gray-50 rounded-lg p-3">
            {info.valorTotal != null && (
              <div className="flex items-center gap-2">
                <span className="text-gray-400">Valor pago:</span>
                <span style={{ fontSize: 14, fontWeight: 700 }}>{formatMoeda(info.valorTotal)}</span>
              </div>
            )}
            {refVenda && (
              <div><span className="text-gray-400">Venda:</span> <span className="font-mono font-medium">{refVenda}</span></div>
            )}
          </div>

          <div className="mt-3 text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            {info.notaAutorizada
              ? 'A Nota Fiscal desta compra já foi emitida — este documento não a substitui. Use só como comprovante do pagamento.'
              : 'A Nota Fiscal será emitida assim que o sistema da SEFAZ normalizar. Use este comprovante como prova do pagamento.'}
          </div>

          {/* Imprimir — cartao default + link tradicional */}
          <button
            type="button"
            onClick={() => void imprimir('cartao')}
            disabled={imprimindo}
            className="mt-3 w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {imprimindo ? <Loader2 size={16} className="animate-spin" /> : <Receipt size={16} />}
            Imprimir comprovante (Maquina de Cartao)
          </button>
          <button
            type="button"
            onClick={() => void imprimir('tradicional')}
            disabled={imprimindo}
            className="mt-1.5 w-full text-xs text-gray-500 hover:text-gray-700 hover:underline disabled:opacity-50"
          >
            ou imprimir no formato tradicional (8cm)
          </button>

          {/* Baixar PDF */}
          <button
            type="button"
            onClick={() => void baixarPdf()}
            disabled={baixando}
            className="mt-3 w-full px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {baixando ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            Baixar PDF
          </button>

          {/* Enviar WhatsApp — SEMPRE pelo seletor de destinatario (DA-347). O whatsapp do
              cadastro entra pre-selecionado no seletor; escolher e confirmar e de quem esta
              com o cliente na frente. */}
          <button
            type="button"
            onClick={() => setModalBuscaAberto(true)}
            className="mt-3 w-full px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
          >
            <Smartphone size={16} />
            {enviadoPara ? 'Enviar novamente' : 'Enviar WhatsApp'}
          </button>

          {enviadoPara && (
            <div className="mt-3 w-full px-4 py-2.5 bg-green-50 border border-green-200 rounded-lg text-center text-sm text-green-700">
              Comprovante enviado para: <span className="font-bold">{enviadoPara}</span>
            </div>
          )}

          {/* Fechar */}
          <button
            type="button"
            onClick={onFechar}
            className="mt-4 w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 text-sm"
          >
            <X size={14} />
            Fechar
          </button>
        </div>
      </div>

      {/* Seletor de destinatario */}
      {modalBuscaAberto && (
        <ModalBuscaContatoComprovante
          orcamentoId={orcamentoId}
          aviso={info.clienteNome && info.clienteNome !== 'Consumidor'
            ? `Confira para quem vai o comprovante de ${info.clienteNome}.`
            : 'Selecione o contato para envio.'}
          preSelecionarTelefone={info.clienteWhatsapp ?? null}
          onClose={(enviado, display) => {
            setModalBuscaAberto(false);
            if (enviado) setEnviadoPara(display ?? 'contato selecionado');
          }}
        />
      )}
    </>,
    document.body,
  );
}

// --------------------------------------------------------------------------
// Seletor de destinatario — posta no /api/comprovante-venda/:orcId/enviar-whatsapp.
// Caminho UNICO do envio (DA-347): o whatsapp do cadastro entra pre-selecionado, mas
// quem confirma e o operador. Devolve o nome/numero pra quem chamou carimbar na tela.
// --------------------------------------------------------------------------
function ModalBuscaContatoComprovante({
  orcamentoId, aviso, preSelecionarTelefone, onClose,
}: {
  orcamentoId: number;
  aviso?: string;
  preSelecionarTelefone?: string | null;
  onClose: (enviado?: boolean, display?: string) => void;
}) {
  const [busca, setBusca] = useState('');
  const [contatos, setContatos] = useState<ContatoSugestao[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [contatoSelecionado, setContatoSelecionado] = useState<ContatoSugestao | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function dedupPorTelefone(lista: ContatoSugestao[]): ContatoSugestao[] {
    const porDigits = new Map<string, ContatoSugestao>();
    for (const c of lista) {
      const digits = (c.telefone ?? '').replace(/\D/g, '');
      if (!digits) continue;
      const existente = porDigits.get(digits);
      if (!existente) { porDigits.set(digits, c); continue; }
      const novoMelhor = (!!c.foto_url && !existente.foto_url) || (!!c.push_name && !existente.push_name);
      if (novoMelhor) porDigits.set(digits, c);
    }
    return [...porDigits.values()];
  }

  useEffect(() => {
    let cancelado = false;
    (async () => {
      setBuscando(true);
      try {
        const preDigits = (preSelecionarTelefone ?? '').replace(/\D/g, '');
        const params = preDigits
          ? `?busca=${encodeURIComponent(preDigits)}&sem_grupo=true&inbox=atendimento&limit=20`
          : `?sem_grupo=true&inbox=atendimento&limit=20`;
        const lista = await api.get<ContatoSugestao[]>(`/api/contatos${params}`);
        if (cancelado) return;
        const filtrada = dedupPorTelefone(lista.filter((c) => c.telefone));
        setContatos(filtrada);
        if (preDigits && filtrada.length > 0) setContatoSelecionado(filtrada[0]!);
        else setTimeout(() => inputRef.current?.focus(), 100);
      } catch {
        // ignora
      } finally {
        if (!cancelado) setBuscando(false);
      }
    })();
    return () => { cancelado = true; };
  }, [preSelecionarTelefone]);

  function handleBuscaChange(e: React.ChangeEvent<HTMLInputElement>) {
    const valor = e.target.value;
    setBusca(valor);
    setContatoSelecionado(null);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setBuscando(true);
      try {
        const params = valor.trim().length >= 2
          ? `?busca=${encodeURIComponent(valor.trim())}&sem_grupo=true&inbox=atendimento&limit=30`
          : `?sem_grupo=true&inbox=atendimento&limit=20`;
        const lista = await api.get<ContatoSugestao[]>(`/api/contatos${params}`);
        setContatos(dedupPorTelefone(lista.filter((c) => c.telefone)));
      } catch {
        setContatos([]);
      } finally {
        setBuscando(false);
      }
    }, 400);
  }

  async function handleEnviar() {
    if (!contatoSelecionado?.telefone) return;
    setEnviando(true);
    setErro(null);
    try {
      const digits = contatoSelecionado.telefone.replace(/\D/g, '');
      await api.post(`/api/comprovante-venda/${orcamentoId}/enviar-whatsapp`, { jid: digits });
      setEnviado(true);
      const nome = contatoSelecionado.nome || contatoSelecionado.push_name;
      const display = nome ? `${nome} - ${formatPhone(digits)}` : formatPhone(digits);
      setTimeout(() => onClose(true, display), 1500);
    } catch (err) {
      setErro((err as Error)?.message || 'Falha ao enviar');
    } finally {
      setEnviando(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[80]">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-800">Enviar comprovante pelo WhatsApp</h3>
          <button onClick={() => onClose(false)} className="p-1 hover:bg-gray-100 rounded-lg transition" aria-label="Fechar">
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        <div className="p-5">
          {aviso && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 mb-3 text-sm text-amber-700">
              <AlertTriangle size={16} className="shrink-0" />
              <span>{aviso}</span>
            </div>
          )}

          <div className="relative mb-3">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={busca}
              onChange={handleBuscaChange}
              placeholder="Buscar contato por nome ou telefone..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none"
            />
            {buscando && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />}
          </div>

          <div className="min-h-[200px] max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
            {contatos.length === 0 && !buscando && (
              <div className="text-center text-gray-400 text-sm py-16">
                {busca.length >= 2 ? 'Nenhum contato encontrado' : 'Nenhum contato recente'}
              </div>
            )}
            {contatos.map((c) => {
              const nome = c.nome || c.push_name || 'Sem nome';
              const selecionado = contatoSelecionado?.id === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setContatoSelecionado(c)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-green-50 transition border-b border-gray-100 last:border-0 ${selecionado ? 'bg-green-50 ring-1 ring-green-400' : ''}`}
                >
                  {c.foto_url ? (
                    <img src={c.foto_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center">
                      <User size={16} className="text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-800 truncate">{nome}</div>
                    <div className="text-xs text-gray-400">{formatPhone((c.telefone ?? '').replace(/\D/g, ''))}</div>
                  </div>
                  {selecionado && (
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {erro && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{erro}</div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200">
          <button onClick={() => onClose(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition">
            Cancelar
          </button>
          <button
            onClick={() => void handleEnviar()}
            disabled={!contatoSelecionado || enviando || enviado}
            className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition ${
              enviado ? 'bg-green-100 text-green-700'
              : contatoSelecionado && !enviando ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {enviado ? <>Enviado!</> : enviando ? <><Loader2 size={16} className="animate-spin" />Enviando...</> : <><Send size={16} />Enviar</>}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
